import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { fetchTrackMixAppearances } from '../../redux/thunk/djpalThunk';
import { setMixQuery, setArtistQuery, setActiveSearch, setMixResults } from '../../redux/slices/djpalSlice';
import { searchMixes, searchArtist } from '../../redux/thunk/djpalThunk';
import MultiArtistText from '../MultiArtistText';
import { canPlayMix, useLazyMixResolveMode, useMixPlayback, delayedClick } from './MixPlayer';
import DJPalScrollableList from './DJPalScrollableList';
import MixLikeButton from '../MixLikeButton';
import MixContextMenu from '../MixContextMenu';
import useRowContextMenu from '../../hooks/useRowContextMenu';
import { isWeb as _isWeb, notifyWebOnly } from '../../utils/webOnly';
import { resolveMixCover, useNativeMixCoverMode } from '../../utils/nativeMixCover';
import { ReactComponent as PlayIcon } from '../../assets/icons/play.svg';
import { ReactComponent as PauseIcon } from '../../assets/icons/pause.svg';
import { entityId } from '../../utils/entityId';
import CoverArt from '../CoverArt';

// `excludeArtist` ({ name, id }): every play except that artist's own mixes — the artist page's
// "Their tracks played by others" count opens this, so the list matches the row's number.
const TrackMixAppearancesModal = ({ track, subjectArtist, excludeArtist = null, onClose }) => {
    const dispatch = useDispatch();
    // Re-render when the server-sourced lazy-mix mode flips.
    useLazyMixResolveMode();
    useNativeMixCoverMode(); // re-render when the native cover flag flips
    const [state, setState] = useState({ mixes: [], pagination: null, loading: true, error: null });
    const [loadingMore, setLoadingMore] = useState(false);
    // Right-click on a mix row opens the same menu as its ⋯ button.
    const { openRowCtx, rowCtxFor } = useRowContextMenu();
    const { playMix, loadingMixId, playingMixId } = useMixPlayback();
    const isWeb = _isWeb;

    const trackLabel = track?.artist && track?.title ? `${track.artist} – ${track.title}` : (track?.title || '');
    const heading = subjectArtist?.name
        ? `${subjectArtist.name}'s mixes featuring this track`
        : excludeArtist ? 'Other DJs who played this track' : 'DJs who played this track';
    const excludeArtistId = !subjectArtist && excludeArtist ? entityId(excludeArtist) : undefined;

    const trackPublicId = entityId(track);
    const artistPublicId = entityId(subjectArtist);
    const artistName = subjectArtist?.name;

    useEffect(() => {
        let cancelled = false;
        setState({ mixes: [], pagination: null, loading: true, error: null });
        (async () => {
            try {
                const result = await dispatch(fetchTrackMixAppearances({
                    trackPublicId,
                    artistPublicId: subjectArtist ? artistPublicId : undefined,
                    artistName: subjectArtist ? artistName : undefined,
                    excludeArtistId,
                    page: 1,
                    limit: 50,
                })).unwrap();
                if (cancelled) return;
                setState({ mixes: result.mixes, pagination: result.pagination, loading: false, error: null });
            } catch (err) {
                if (cancelled) return;
                setState({ mixes: [], pagination: null, loading: false, error: err?.message || 'Failed to load' });
            }
        })();
        return () => { cancelled = true; };
    }, [dispatch, trackPublicId, artistPublicId, artistName, subjectArtist, excludeArtistId]);

    const handleLoadMore = useCallback(async () => {
        if (loadingMore || !state.pagination?.hasNext) return;
        setLoadingMore(true);
        try {
            const result = await dispatch(fetchTrackMixAppearances({
                trackPublicId,
                artistPublicId: subjectArtist ? artistPublicId : undefined,
                artistName: subjectArtist ? artistName : undefined,
                excludeArtistId,
                page: (state.pagination.page || 1) + 1,
                limit: 50,
            })).unwrap();
            setState(prev => ({
                mixes: [...prev.mixes, ...result.mixes],
                pagination: result.pagination,
                loading: false,
                error: null,
            }));
        } catch (err) {
            console.error('Load more mix appearances failed:', err);
        } finally {
            setLoadingMore(false);
        }
    }, [dispatch, loadingMore, state.pagination, trackPublicId, artistPublicId, artistName, subjectArtist, excludeArtistId]);

    const handleMixClick = useCallback((mix) => {
        // Id-only navigation (link = exact-key fallback); clicks never
        // text-search.
        const filter = entityId(mix) ? { public_id: entityId(mix) }
            : mix.link ? { link: mix.link }
            : null;
        if (!filter) return;
        dispatch(setMixQuery(mix.title || ''));
        dispatch(setActiveSearch('mix'));
        dispatch(searchMixes({ filters: filter }));
        onClose();
    }, [dispatch, onClose]);

    const handleArtistClick = useCallback((a) => {
        if (!a?.name) return;
        dispatch(setArtistQuery(a.name));
        dispatch(setActiveSearch('artist'));
        dispatch(searchArtist({ artistName: a.name, artistId: entityId(a) || null, artworkUrl: a.artworkUrl || null }));
        onClose();
    }, [dispatch, onClose]);

    const handleMixPlay = useCallback((mix) => {
        if (isWeb) {
            notifyWebOnly('mix');
            return;
        }
        playMix(mix);
    }, [isWeb, playMix]);

    // Esc closes
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return ReactDOM.createPortal(
        <div className="djpal-modal-backdrop" onMouseDown={onClose}>
            <div className="djpal-modal djpal-mix-appearances-modal" onMouseDown={e => e.stopPropagation()}>
                <div className="djpal-modal-header">
                    <div className="djpal-modal-title-group">
                        <h3 className="djpal-modal-title">{heading}</h3>
                        <div className="djpal-modal-subtitle" title={trackLabel}>{trackLabel}</div>
                    </div>
                    <button className="djpal-modal-close" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="djpal-modal-body">
                    {state.loading && (
                        <div className="djpal-modal-skel" aria-label="Loading">
                            {[...Array(5).keys()].map((i) => (
                                <div key={i} className="djpal-modal-skel-row">
                                    <span className="djpal-modal-skel-art" />
                                    <span className="djpal-modal-skel-text">
                                        <span className="djpal-modal-skel-line" />
                                        <span className="djpal-modal-skel-line short" />
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                    {!state.loading && state.error && (
                        <div className="djpal-empty-hint">Couldn't load mixes: {state.error}</div>
                    )}
                    {!state.loading && !state.error && state.mixes.length === 0 && (
                        <div className="djpal-empty-hint">No mixes found.</div>
                    )}
                    {!state.loading && !state.error && state.mixes.length > 0 && (
                        <DJPalScrollableList maxHeight="60vh" onScrollBottom={handleLoadMore}>
                            {state.mixes.map((mix, i) => {
                                const mixArt = resolveMixCover(mix);
                                const mixLabel = (mix.djName || mix.artist) ? `${mix.djName || mix.artist} - ${mix.title}` : mix.title;
                                const isLoading = loadingMixId === mix.id;
                                const isPlaying = playingMixId === mix.id;
                                const canPlay = canPlayMix(mix);
                                return (
                                    <div
                                        className="djpal-track-row clickable"
                                        key={`mix-app-${entityId(mix) || i}`}
                                        onContextMenu={openRowCtx(`mix-app-${entityId(mix) || i}`)}
                                        {...delayedClick(() => handleMixClick(mix))}
                                    >
                                        <div
                                            className={`track-artwork${canPlay ? ' clickable' : ''}`}
                                            onClick={canPlay ? (e) => { e.stopPropagation(); handleMixPlay(mix); } : undefined}
                                        >
                                            {canPlay && (
                                                <div className={`mix-play-overlay${isLoading ? ' loading' : ''}${isPlaying ? ' playing' : ''}`}>
                                                    {isLoading ? <span className="mix-play-spinner" /> : <span className={`play-icon${isPlaying ? ' paused' : ''}`}>{isPlaying ? <PauseIcon /> : <PlayIcon />}</span>}
                                                </div>
                                            )}
                                            {mixArt ? <CoverArt src={mixArt} size={48} /> : <div className="artwork-placeholder">&#9835;</div>}
                                        </div>
                                        <div className="track-info">
                                            <span className="track-label" title={mixLabel}>{mix.title || mixLabel}</span>
                                            {(mix.artists?.length > 0 || mix.djName || mix.artist || mix.date) && (
                                                <span className="track-sublabel track-artist-line">
                                                    <MultiArtistText
                                                        artists={mix.artists}
                                                        fallbackText={mix.djName || mix.artist}
                                                        segmentClassName="clickable-artist"
                                                        onSelect={handleArtistClick}
                                                    />
                                                    {(mix.artists?.length > 0 || mix.djName || mix.artist) && mix.date ? ' · ' : ''}
                                                    {mix.date}
                                                </span>
                                            )}
                                        </div>
                                        <MixLikeButton mix={mix} />
                                        <MixContextMenu mix={mix} openAtPoint={rowCtxFor(`mix-app-${entityId(mix) || i}`)} />
                                    </div>
                                );
                            })}
                            {loadingMore && <div className="djpal-load-more-spinner"><span className="mix-play-spinner" /></div>}
                        </DJPalScrollableList>
                    )}
                </div>
                {/* Always mounted: a footer that appears only once the count
                    arrives shifted the whole modal after first paint. */}
                <div className="djpal-modal-footer">
                    {state.pagination?.totalCount != null && state.mixes.length > 0
                        ? `Showing ${state.mixes.length} of ${state.pagination.totalCount}`
                        : '\u00A0'}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default TrackMixAppearancesModal;
